import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { buildBackendUrl } from '@/lib/auth-routing';

interface RenderCacheBackfillReport {
  processed: number;
  updated: number;
  skipped: number;
  errors: { snapshotId: string; message: string }[];
}

async function postBackfill(dryRun: boolean): Promise<RenderCacheBackfillReport> {
  const response = await fetch(
    buildBackendUrl('/api/admin/artifact-snapshots/render-cache/backfill'),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ dryRun }),
    }
  );

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Backfill failed with ${String(response.status)}: ${body}`);
  }

  return response.json() as Promise<RenderCacheBackfillReport>;
}

function reportLabel(report: RenderCacheBackfillReport): string {
  const errorSuffix = report.errors.length > 0 ? `, ${String(report.errors.length)} errors` : '';
  return `${String(report.processed)} processed, ${String(report.updated)} updated, ${String(report.skipped)} skipped${errorSuffix}`;
}

export function AdminRenderCacheBackfillButton(): React.ReactElement {
  const queryClient = useQueryClient();
  const [lastReport, setLastReport] = useState<RenderCacheBackfillReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDryRun, setPendingDryRun] = useState<RenderCacheBackfillReport | null>(null);

  const dryRunMutation = useMutation({
    mutationFn: () => postBackfill(true),
    onSuccess: report => {
      void queryClient.invalidateQueries({ queryKey: ['artifact'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setError(null);
      if (report.processed === 0) {
        setLastReport(report);
        setPendingDryRun(null);
      } else {
        setPendingDryRun(report);
      }
    },
    onError: caught => {
      setPendingDryRun(null);
      setError(caught instanceof Error ? caught.message : 'Render-cache backfill failed');
    },
  });

  const applyMutation = useMutation({
    mutationFn: () => postBackfill(false),
    onSuccess: report => {
      void queryClient.invalidateQueries({ queryKey: ['artifact'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setError(null);
      setLastReport(report);
      setPendingDryRun(null);
    },
    onError: caught => {
      setPendingDryRun(null);
      setError(caught instanceof Error ? caught.message : 'Render-cache backfill failed');
    },
  });

  const isBusy = dryRunMutation.isPending || applyMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Render Cache Backfill</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={() => {
              dryRunMutation.mutate();
            }}
            disabled={isBusy}
          >
            <RefreshCw className="mr-2 size-4" />
            {isBusy ? 'Checking…' : 'Backfill'}
          </Button>
          {lastReport && (
            <span className="text-sm text-muted-foreground">{reportLabel(lastReport)}</span>
          )}
        </div>
        {error && <div className="text-sm text-destructive">{error}</div>}
      </CardContent>

      <AlertDialog
        open={pendingDryRun !== null}
        onOpenChange={open => {
          if (!open) setPendingDryRun(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Backfill render caches?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDryRun
                ? `${String(pendingDryRun.processed)} artifact snapshot${pendingDryRun.processed === 1 ? '' : 's'} will be re-rendered. This rewrites the markdown/html/plain-text caches in place.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                applyMutation.mutate();
              }}
            >
              Backfill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
